/**
 * @file `useSignoutMutation` — POST /api/account/signout, then flip
 *       `qkMe` to `{ admin: null }` synchronously.
 * @module @labcraft/admin/ui/hooks/use-signout
 *
 * The synchronous `setQueryData(qkMe, { admin: null })` in
 * `onSuccess` is load-bearing — it's the fix for the documented
 * post-signout chrome-bounce regression. `invalidateQueries` would
 * mark stale and refetch on the next mount: between the signout
 * response and the refetch resolving, one render goes through with
 * the dashboard still mounted under the now-signed-out chrome. The
 * imperative cache write skips that race entirely.
 *
 * Convention: hooks live in this directory and never call
 * `useMutation` outside it.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useClient } from '@labcraft/core/frontend/react'
import type { Admin } from '../../client/index.ts'
import { qkMe } from './query_keys.ts'

/**
 * Sign the current admin out. The mutation's `onSuccess` writes
 * `{ admin: null }` into the `qkMe` cache so the next render flips
 * the chrome to the anonymous branch before the dashboard can
 * re-render with the stale identity.
 */
export function useSignoutMutation() {
  const client = useClient<Admin>()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => client.signout(),
    onSuccess: () => {
      queryClient.setQueryData(qkMe, { admin: null })
    },
  })
}
